import { Component, ReactElement } from 'react';
import _ from 'lodash';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { SettingsList } from '@Primitive/SettingsList';
import { SettingsGroup } from '@Primitive/SettingsListType';
import { TooltipPosition } from '@Primitive/ToolbarIconButton';
import { isDefined, IsNullOrWhiteSpace, isFunction } from '@Utils/Micro';
import { ItemsStyle, GetReactStyle } from '@Utils/Colors';
import { setDynamicFormDataPaths } from '@UI/Widgets/Things/UserInput/DynamicForm';
import { Props } from '@Utils/ReactProps';

// ------------------------------------------------------------------------------------------------
export interface IAdvancedTippy {
    width: number;
    height: number;
    elements: SettingsGroup[];
    data: { [key: string]: any };
    style: ItemsStyle;
}
// ------------------------------------------------------------------------------------------------
interface AdvancedTippyProps extends Props<AdvancedTippy> {
    placement: TooltipPosition;
    tooltip: string;
    advancedTooltip: IAdvancedTippy;
    advancedPopup: IAdvancedTippy;
    iconClicked: (...args: any[]) => void;
    buttonPressed: (element: any, data: any) => void;
    elementChanged: (dataPath: string, data: any, prevData: any, formData: any) => void;
}
// ------------------------------------------------------------------------------------------------
interface AdvancedTippyState {
    openTooltip: boolean;
    openPopup: boolean;
}
// ------------------------------------------------------------------------------------------------
export class AdvancedTippy extends Component<AdvancedTippyProps, AdvancedTippyState> {
    constructor(props: AdvancedTippyProps) {
        super(props);
        this.state = {
            openTooltip: false,
            openPopup: false
        };
    }

    get hasTooltip(): boolean {
        return !IsNullOrWhiteSpace(this.props.tooltip) || this.hasAdvancedTooltip;
    }

    get hasAdvancedTooltip(): boolean {
        return isDefined(this.props.advancedTooltip);
    }

    get hasAdvancedPopup(): boolean {
        return isDefined(this.props.advancedPopup);
    }

    get hasAdvancedContent(): boolean {
        return this.hasAdvancedTooltip || this.hasAdvancedPopup;
    }

    applyUniformStyling = (element: HTMLElement, eventType: string) => {
        // only for advanced tippy
        if (!this.hasAdvancedContent) {
            return;
        }

        // reset color to outer box and arrow, add padding
        let box: HTMLElement = element.querySelector('.tippy-box');
        box.style.backgroundColor = '#606060';

        let content: HTMLElement = element.querySelector('.tippy-content');
        content.style.padding = '5px 9px';

        let arrow: HTMLElement = element.querySelector('.tippy-arrow');
        arrow.style.color = '#606060';

        // get background color of advanced content
        let color: string;
        if (eventType == 'mouseenter') {
            color = this.props.advancedTooltip?.style?.backgroundColor;
        } else if (eventType == 'click') {
            color = this.props.advancedPopup?.style?.backgroundColor;
        }

        // set color to outer box and arrow, remove padding
        if (isDefined(color)) {
            box.style.backgroundColor = color;
            content.style.padding = '0px';
            arrow.style.color = color;
        }
    }

    getInnerContent = () => {
        const getAdvancedContent = (advancedTippy: IAdvancedTippy) => {
            // set settings list
            let data = setDynamicFormDataPaths(advancedTippy.elements, { ...advancedTippy.data });
            return (
                <div style={{
                    width: advancedTippy.width,
                    height: advancedTippy.height,
                    maxWidth: advancedTippy.width,
                    maxHeight: advancedTippy.height,
                    overflow: 'auto',
                    borderRadius: 4,
                    ...GetReactStyle(advancedTippy.style)
                }}>
                    <SettingsList
                        AutoSave
                        SettingsMap={advancedTippy.elements}
                        Data={data}
                        onSaveData={undefined}
                        onButtonPressed={this.props.buttonPressed}
                        onElementChanged={this.props.elementChanged} />
                </div>
            );
        }

        // set inner content
        let content = null;
        if (this.state.openPopup) {
            let advancedPopup = this.props.advancedPopup;

            if (isDefined(advancedPopup)) {
                content = getAdvancedContent(advancedPopup);
            }
        } else if (this.state.openTooltip) {
            let tooltip = this.props.tooltip;
            let advancedTooltip = this.props.advancedTooltip;

            if (isDefined(advancedTooltip)) {
                content = getAdvancedContent(advancedTooltip);
            } else if (!IsNullOrWhiteSpace(tooltip)) {
                content = tooltip;
            }
        }
        return content;
    }

    render() {
        // which is the trigger?
        let triggers: string[] = [];
        if (this.hasTooltip) {
            triggers.push('mouseenter');
        }
        if (this.hasAdvancedPopup) {
            triggers.push('click');
        }

        return (
            <Tippy
                zIndex={1500}
                animation={false}
                placement={this.props.placement}
                appendTo={document.body}
                interactive={this.state.openPopup}
                hideOnClick={false}
                trigger={_.join(triggers, ' ')}
                onTrigger={(instance, event) => {
                    // reset the opacity which was zero to solve artifacts
                    instance.popper.style.opacity = '1';

                    switch (event.type) {
                        case 'mouseenter':
                            // don't show if popup is visible
                            if (!this.state.openPopup) {
                                this.applyUniformStyling(instance.popper, event.type);
                                this.setState({ openTooltip: true });
                            }
                            break;
                        case 'click':
                            this.applyUniformStyling(instance.popper, event.type);
                            this.setState({ openTooltip: false, openPopup: true });
                            break;
                        default:
                            break;
                    }
                }}
                onUntrigger={(instance, event) => {
                    if (event.type == 'mouseleave' && !this.state.openPopup) {
                        // set a zero opacity to solve artifacts
                        instance.popper.style.opacity = '0';
                        this.setState({ openTooltip: false });
                    }
                }}
                onShow={() => {
                    // is it an ios device?
                    if (/iPhone|iPad|iPod/.test(navigator.platform) &&
                        isFunction(this.props.iconClicked))
                        this.props.iconClicked();
                }}
                onClickOutside={(instance, event) => {
                    if ((event.target as Element).id?.indexOf('react-select') == -1) {
                        // set a zero opacity to solve artifacts
                        instance.popper.style.opacity = '0';
                        instance.hide();
                        this.setState({ openPopup: false });
                    }
                }}
                content={this.getInnerContent()}>
                {this.props.children as ReactElement}
            </Tippy>
        );
    }
}
// ------------------------------------------------------------------------------------------------